/**
 * Countries a company can be registered in, each with the IANA time zones offered for it. Shared
 * between `willnq-web` and (future) `willnq-mobile`.
 *
 * <p>This exists because a company's country and its time zone are ONE fact, not two. Offering the
 * runtime's full IANA list (400+ zones) alongside a free-text country let a registration claim
 * "India" in `Asia/Anadyr` — a contradiction nothing validated, for a pair of fields whose only
 * consumer is a row in the reviewer's detail dialog. Deriving the zone from the country makes that
 * state unrepresentable.</p>
 *
 * <p><b>Curated, not ISO 3166 in full.</b> WillnQ is an India-first platform, and this list covers
 * India plus the markets Indian employers actually hire across. The reason it is curated rather than
 * exhaustive is accuracy, not effort: a hand-authored zone table for all ~249 territories would
 * contain mistakes, and a wrong stored zone is worse than an absent country — adding one here is a
 * single entry, whereas a wrong entry silently mis-stamps every company registered under it.</p>
 *
 * <p><b>Only genuinely multi-zone countries offer a choice.</b> A country with one entry needs no
 * question asked; the form derives it. "Multiple" means zones in common civil use, so New Zealand is
 * single (the Chatham Islands' separate zone serves a few hundred people) and China is single (only
 * Beijing time is official). Where a country does split, the zones are ordered by population so the
 * first is the sensible default.</p>
 *
 * <p>Labels name the region a person would recognise, not the IANA identifier — `Asia/Kolkata` is a
 * database key, "India Standard Time (IST)" is what a founder is choosing.</p>
 */

/** One selectable time zone: the IANA identifier that gets stored, and how it reads to a human. */
export interface CountryTimezone {
  /** IANA identifier, e.g. `Asia/Kolkata`. This is the value persisted on the registration. */
  readonly id: string;
  readonly label: string;
}

export interface Country {
  /**
   * ISO 3166-1 alpha-2. Not persisted today — the registration's `country` column holds the display
   * name, as it did when the field was free text, so existing rows stay readable and no migration is
   * needed. Kept here as the stable identity for the entry if a code ever needs storing.
   */
  readonly code: string;
  /** The display name, and the value stored in `company_registrations.country`. */
  readonly name: string;
  /** Zones offered for this country. Exactly one means "derived — do not ask". */
  readonly timezones: readonly CountryTimezone[];
}

/** India leads (the home market); the rest are alphabetical. */
export const COUNTRIES: readonly Country[] = [
  { code: 'IN', name: 'India', timezones: [{ id: 'Asia/Kolkata', label: 'India Standard Time (IST)' }] },

  {
    code: 'AU',
    name: 'Australia',
    timezones: [
      { id: 'Australia/Sydney', label: 'Sydney / Melbourne / Canberra (Eastern)' },
      { id: 'Australia/Brisbane', label: 'Brisbane (Eastern, no daylight saving)' },
      { id: 'Australia/Adelaide', label: 'Adelaide (Central)' },
      { id: 'Australia/Darwin', label: 'Darwin (Central, no daylight saving)' },
      { id: 'Australia/Perth', label: 'Perth (Western)' },
      { id: 'Australia/Hobart', label: 'Hobart (Tasmania)' },
    ],
  },
  { code: 'BH', name: 'Bahrain', timezones: [{ id: 'Asia/Bahrain', label: 'Arabian Standard Time' }] },
  { code: 'BD', name: 'Bangladesh', timezones: [{ id: 'Asia/Dhaka', label: 'Bangladesh Standard Time' }] },
  {
    code: 'BR',
    name: 'Brazil',
    timezones: [
      { id: 'America/Sao_Paulo', label: 'São Paulo / Brasília' },
      { id: 'America/Manaus', label: 'Manaus (Amazon)' },
      { id: 'America/Rio_Branco', label: 'Rio Branco (Acre)' },
    ],
  },
  {
    code: 'CA',
    name: 'Canada',
    timezones: [
      { id: 'America/Toronto', label: 'Toronto / Ottawa (Eastern)' },
      { id: 'America/Vancouver', label: 'Vancouver (Pacific)' },
      { id: 'America/Edmonton', label: 'Calgary / Edmonton (Mountain)' },
      { id: 'America/Winnipeg', label: 'Winnipeg (Central)' },
      { id: 'America/Halifax', label: 'Halifax (Atlantic)' },
      { id: 'America/St_Johns', label: "St John's (Newfoundland)" },
    ],
  },
  { code: 'CN', name: 'China', timezones: [{ id: 'Asia/Shanghai', label: 'China Standard Time' }] },
  { code: 'EG', name: 'Egypt', timezones: [{ id: 'Africa/Cairo', label: 'Eastern European Time' }] },
  { code: 'FR', name: 'France', timezones: [{ id: 'Europe/Paris', label: 'Central European Time' }] },
  { code: 'DE', name: 'Germany', timezones: [{ id: 'Europe/Berlin', label: 'Central European Time' }] },
  { code: 'HK', name: 'Hong Kong SAR', timezones: [{ id: 'Asia/Hong_Kong', label: 'Hong Kong Time' }] },
  {
    code: 'ID',
    name: 'Indonesia',
    timezones: [
      { id: 'Asia/Jakarta', label: 'Jakarta (Western — WIB)' },
      { id: 'Asia/Makassar', label: 'Makassar (Central — WITA)' },
      { id: 'Asia/Jayapura', label: 'Jayapura (Eastern — WIT)' },
    ],
  },
  { code: 'IE', name: 'Ireland', timezones: [{ id: 'Europe/Dublin', label: 'Irish Standard Time' }] },
  { code: 'IL', name: 'Israel', timezones: [{ id: 'Asia/Jerusalem', label: 'Israel Standard Time' }] },
  { code: 'IT', name: 'Italy', timezones: [{ id: 'Europe/Rome', label: 'Central European Time' }] },
  { code: 'JP', name: 'Japan', timezones: [{ id: 'Asia/Tokyo', label: 'Japan Standard Time' }] },
  { code: 'KE', name: 'Kenya', timezones: [{ id: 'Africa/Nairobi', label: 'East Africa Time' }] },
  { code: 'KW', name: 'Kuwait', timezones: [{ id: 'Asia/Kuwait', label: 'Arabian Standard Time' }] },
  { code: 'MY', name: 'Malaysia', timezones: [{ id: 'Asia/Kuala_Lumpur', label: 'Malaysia Time' }] },
  {
    code: 'MX',
    name: 'Mexico',
    timezones: [
      { id: 'America/Mexico_City', label: 'Mexico City (Central)' },
      { id: 'America/Cancun', label: 'Cancún (Eastern)' },
      { id: 'America/Mazatlan', label: 'Mazatlán (Mexican Pacific)' },
      { id: 'America/Tijuana', label: 'Tijuana (Pacific)' },
    ],
  },
  { code: 'NP', name: 'Nepal', timezones: [{ id: 'Asia/Kathmandu', label: 'Nepal Time' }] },
  { code: 'NL', name: 'Netherlands', timezones: [{ id: 'Europe/Amsterdam', label: 'Central European Time' }] },
  { code: 'NZ', name: 'New Zealand', timezones: [{ id: 'Pacific/Auckland', label: 'New Zealand Time' }] },
  { code: 'NG', name: 'Nigeria', timezones: [{ id: 'Africa/Lagos', label: 'West Africa Time' }] },
  { code: 'OM', name: 'Oman', timezones: [{ id: 'Asia/Muscat', label: 'Gulf Standard Time' }] },
  { code: 'PK', name: 'Pakistan', timezones: [{ id: 'Asia/Karachi', label: 'Pakistan Standard Time' }] },
  { code: 'PH', name: 'Philippines', timezones: [{ id: 'Asia/Manila', label: 'Philippine Time' }] },
  { code: 'PL', name: 'Poland', timezones: [{ id: 'Europe/Warsaw', label: 'Central European Time' }] },
  {
    code: 'PT',
    name: 'Portugal',
    timezones: [
      { id: 'Europe/Lisbon', label: 'Lisbon (mainland)' },
      { id: 'Atlantic/Azores', label: 'Azores' },
    ],
  },
  { code: 'QA', name: 'Qatar', timezones: [{ id: 'Asia/Qatar', label: 'Arabian Standard Time' }] },
  { code: 'SA', name: 'Saudi Arabia', timezones: [{ id: 'Asia/Riyadh', label: 'Arabian Standard Time' }] },
  { code: 'SG', name: 'Singapore', timezones: [{ id: 'Asia/Singapore', label: 'Singapore Time' }] },
  { code: 'ZA', name: 'South Africa', timezones: [{ id: 'Africa/Johannesburg', label: 'South Africa Time' }] },
  { code: 'KR', name: 'South Korea', timezones: [{ id: 'Asia/Seoul', label: 'Korea Standard Time' }] },
  {
    code: 'ES',
    name: 'Spain',
    timezones: [
      { id: 'Europe/Madrid', label: 'Madrid (peninsular)' },
      { id: 'Atlantic/Canary', label: 'Canary Islands' },
    ],
  },
  { code: 'LK', name: 'Sri Lanka', timezones: [{ id: 'Asia/Colombo', label: 'Sri Lanka Time' }] },
  { code: 'SE', name: 'Sweden', timezones: [{ id: 'Europe/Stockholm', label: 'Central European Time' }] },
  { code: 'CH', name: 'Switzerland', timezones: [{ id: 'Europe/Zurich', label: 'Central European Time' }] },
  { code: 'TH', name: 'Thailand', timezones: [{ id: 'Asia/Bangkok', label: 'Indochina Time' }] },
  { code: 'TR', name: 'Türkiye', timezones: [{ id: 'Europe/Istanbul', label: 'Türkiye Time' }] },
  { code: 'AE', name: 'United Arab Emirates', timezones: [{ id: 'Asia/Dubai', label: 'Gulf Standard Time' }] },
  { code: 'GB', name: 'United Kingdom', timezones: [{ id: 'Europe/London', label: 'UK Time (GMT/BST)' }] },
  {
    code: 'US',
    name: 'United States',
    timezones: [
      { id: 'America/New_York', label: 'Eastern (New York)' },
      { id: 'America/Chicago', label: 'Central (Chicago)' },
      { id: 'America/Denver', label: 'Mountain (Denver)' },
      { id: 'America/Phoenix', label: 'Mountain – Arizona (no daylight saving)' },
      { id: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
      { id: 'America/Anchorage', label: 'Alaska (Anchorage)' },
      { id: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
    ],
  },
  { code: 'VN', name: 'Vietnam', timezones: [{ id: 'Asia/Ho_Chi_Minh', label: 'Indochina Time' }] },
];

/** The home market, and the form's default. */
export const DEFAULT_COUNTRY_NAME = 'India';

export function findCountry(name: string): Country | undefined {
  return COUNTRIES.find((country) => country.name === name);
}

/** The zones offered for a country — empty for one this list does not carry. */
export function timezonesForCountry(name: string): readonly CountryTimezone[] {
  return findCountry(name)?.timezones ?? [];
}

/**
 * The zone to store for a country when the user is not asked: the only one, or the most populous of
 * several. Returns undefined for an unknown country so a caller cannot silently invent a zone.
 */
export function defaultTimezoneForCountry(name: string): string | undefined {
  return timezonesForCountry(name)[0]?.id;
}

/**
 * True when the country needs a time-zone question at all. One zone means the answer is already
 * known, and asking would be a question with a single possible answer.
 */
export function countryNeedsTimezoneChoice(name: string): boolean {
  return timezonesForCountry(name).length > 1;
}

/**
 * Whether this country/zone pairing is one this list actually permits. The cross-field check that
 * makes "India in Asia/Anadyr" unrepresentable rather than merely unlikely.
 */
export function isTimezoneValidForCountry(countryName: string, timezoneId: string): boolean {
  return timezonesForCountry(countryName).some((timezone) => timezone.id === timezoneId);
}
